---
layout: docs
title: "The Admin Console"
---


The admin console is where operators and developers configure, manage, and monitor AI agents. It's a separate interface from the Hub — designed for building and governance, not end-user chat.

---

## Dashboard

The admin console opens to a dashboard showing your root project and all apps (agents) within it. From here you can:

- See all configured agents at a glance
- Create new apps
- Navigate to any app's detail view
- Access instance-level settings (models, configuration)

---

## App Detail View

Each app has a tabbed interface:

### Build & Test

A debug chat interface. You interact with the agent and see exactly what's happening:

- Which model is being called
- What tools the agent invokes and their responses
- The full reasoning trace
- File system operations

This is the test environment — changes to intelligence, connections, and channels take effect here immediately.

### Intelligence

Configure the agent's brain:

- **Intelligence type** — Dynamic Prompt (production) or Chat Completion
- **Model selection** — choose from enabled models
- **System prompt** — define the agent's personality, role, and constraints
- See [Intelligence Configuration →](../building-agents/intelligence-configuration) for details

### Connections

Add and manage tool integrations:

- **MCP** — connect to MCP servers that expose tools to the agent
- **OpenAPI** — connect to APIs via OpenAPI specifications
- Each connection can be enabled or disabled per app
- See [Tools: MCP →](../building-agents/tools-mcp) and [Tools: OpenAPI →](../building-agents/tools-openapi)

### Channels (Reach)

Configure where the app is accessible:

- **Teams** — enable Teams integration, download the app manifest
- **Web Chat Widget** — configure and generate the embed snippet
- **DirectLine** — always enabled (powers the Hub)
- See [Channels →](../building-agents/channels)

### Analytics

Monitor agent behavior and usage:

- **Conversations** — total count, message counts, duration
- **LLM usage** — token consumption by model
- **Tool calls** — which tools are being called, how often, success rates
- **Usage patterns** — hourly and daily activity trends

### Activities

Browse individual conversation turns — see what happened in each interaction, what tools were called, and how the agent responded.

### Versions

Publish and manage versions:

- Publish a new version to make current test configuration live
- Browse version history
- See [Publishing & Versions →](../building-agents/publishing-and-versions)

---

## Instance-Level Settings

Beyond individual apps, the admin console provides instance-wide management:

### Models

Navigate to **Instance → Models** to:

- Sync available models from your AI Foundry catalog
- Enable or disable models across the instance
- See which models are deployed and their status

---

## Agent Flow Diagram

Each app has a visual agent flow diagram (built with @xyflow/svelte) that shows how the agent is composed — its intelligence type, tools, connections, and sub-agent relationships.

<!-- TODO: Screenshot or description of what the agent flow diagram looks like. How is it useful for debugging or understanding agent behavior? -->

*This section is a work in progress.*


---

## Permissions

Access to the admin console requires the **Management** global role, assigned via Entra ID app role assignment. Within the console, individual app operations are governed by per-node permissions (Read, ReadAndWrite, ReadAndPublish, etc.).

See [Permissions & Access Control →](../security-and-governance/permissions).
